/**
 * AdvancedAnalyticsDashboard - Comprehensive analytics with customer segmentation,
 * revenue trends, cohort analysis, and performance metrics
 * 
 * Refactored to use sub-components for better maintainability
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, Users, Target, RefreshCw, Calendar,
  AlertTriangle, TrendingUp, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { api } from '@/utils/apiClient';
import {
  QuickStatCard,
  AnalyticsOverviewTab,
  SegmentsTab,
  ProductsTab,
  TimeAnalysisTab,
  CampaignsTab
} from './advanced';

export default function AdvancedAnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [segmentation, setSegmentation] = useState(null);
  const [productPerformance, setProductPerformance] = useState(null);
  const [timeInsights, setTimeInsights] = useState(null);
  const [campaignROI, setCampaignROI] = useState(null);
  const [segmentInsights, setSegmentInsights] = useState(null);
  
  // Fetch all analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const days = parseInt(dateRange);
      const [
        overviewRes,
        segmentRes,
        productRes,
        timeRes,
        campaignRes,
        insightsRes
      ] = await Promise.all([
        api.get('/admin/analytics/overview', { params: { days } }),
        api.get('/admin/segmentation/analysis', { params: { days } }),
        api.get('/admin/analytics/product-performance', { params: { days } }),
        api.get('/admin/analytics/time-insights', { params: { days } }),
        api.get('/admin/analytics/campaign-roi', { params: { days } }),
        api.get('/admin/segmentation/insights')
      ]);

      setOverview(overviewRes.data);
      setSegmentation(segmentRes.data);
      setProductPerformance(productRes.data);
      setTimeInsights(timeRes.data);
      setCampaignROI(campaignRes.data);
      setSegmentInsights(insightsRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Run auto-tagging
  const runAutoTagging = async () => {
    try {
      const res = await api.post('/admin/segmentation/auto-tag', null, { params: { days: dateRange } });
      toast.success(`Tagged ${res.data.customers_tagged} customers successfully`);
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to run auto-tagging');
    }
  };

  // Export functions
  const exportSales = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.open(`${baseUrl}/api/admin/analytics/export/sales?days=${dateRange}`, '_blank');
  };
  
  const exportCustomers = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.open(`${baseUrl}/api/admin/analytics/export/customers`, '_blank');
  };
  
  const exportProducts = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.open(`${baseUrl}/api/admin/analytics/export/products?days=${dateRange}`, '_blank');
  };

  // Utility formatters
  const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;
  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advanced-analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Advanced Analytics</h2>
          <p className="text-muted-foreground">Customer segmentation, trends & insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Quick Stats Row */}
      {overview && segmentInsights && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <QuickStatCard
            label="Total Revenue"
            value={formatCurrency(overview.total_revenue)}
            change={overview.revenue_growth}
            icon={DollarSign}
            color="green"
          />
          <QuickStatCard
            label="Total Customers"
            value={segmentInsights.total_segmented_customers || overview.total_customers}
            change={overview.orders_growth}
            icon={Users}
            color="blue"
          />
          <QuickStatCard
            label="At-Risk Revenue"
            value={formatCurrency(segmentInsights.at_risk_revenue)}
            icon={AlertTriangle}
            color="red"
            tooltip="Revenue from at-risk, hibernating, and lost customers"
          />
          <QuickStatCard
            label="Growth Potential"
            value={formatCurrency(segmentInsights.growth_potential_revenue)}
            icon={TrendingUp}
            color="purple"
            tooltip="Revenue from promising and potential loyalist segments"
          />
          <QuickStatCard
            label="Avg Order Value"
            value={formatCurrency(overview.avg_order_value)}
            icon={Target}
            color="orange"
          />
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="segments">Segments</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="time">Time</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <AnalyticsOverviewTab
            segmentation={segmentation}
            productPerformance={productPerformance}
            onViewAllSegments={() => setActiveTab('segments')}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments">
          <SegmentsTab
            segmentation={segmentation}
            onRunAutoTagging={runAutoTagging}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          <ProductsTab
            productPerformance={productPerformance}
            onExportProducts={exportProducts}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        </TabsContent>

        {/* Time Analysis Tab */}
        <TabsContent value="time">
          <TimeAnalysisTab
            timeInsights={timeInsights}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns">
          <CampaignsTab
            campaignROI={campaignROI}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
          />
        </TabsContent>
      </Tabs>

      {/* Export Section */}
      <div className="bg-muted/30 dark:bg-muted/20 rounded-xl p-4 border border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h4 className="font-medium text-foreground">Export Reports</h4>
            <p className="text-sm text-muted-foreground">Download data as CSV files</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportSales} className="gap-2">
              <Download className="w-4 h-4" />
              Sales
            </Button>
            <Button variant="outline" onClick={exportCustomers} className="gap-2">
              <Download className="w-4 h-4" />
              Customers
            </Button>
            <Button variant="outline" onClick={exportProducts} className="gap-2">
              <Download className="w-4 h-4" />
              Products
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
